import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, DatePicker, Select, message, Tag, Space, Typography, Alert, Tooltip, Checkbox } from 'antd';
import { EditOutlined, CopyOutlined, FileTextOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { colors, spacing, radius, typography, shadows } from '../../styles/monochromeTheme';
import { supabase } from '../../api/supabase';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

// Styles
const styles = {
  page: {
    minHeight: '100vh',
    background: colors.bgPrimary,
    padding: spacing.xl,
  },
  container: {
    maxWidth: '1600px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xl,
    paddingBottom: spacing.lg,
    borderBottom: `1px solid ${colors.borderLight}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.md,
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: radius.xl,
    background: colors.bgDark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },
  statsCard: {
    background: colors.bgSecondary,
    borderRadius: radius.xl,
    border: `1px solid ${colors.borderLight}`,
    padding: spacing.lg,
    marginBottom: spacing.lg,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: spacing.lg,
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.textPrimary,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  primaryBtn: {
    background: colors.bgDark,
    color: colors.textInverse,
    border: 'none',
    borderRadius: radius.lg,
    fontWeight: typography.fontWeight.semibold,
    height: '44px',
  },
};

// Nationalités
const NATIONALITIES = [
  { value: 'FRA', label: '🇫🇷 France', code: 'FRA' },
  { value: 'USA', label: '🇺🇸 États-Unis', code: 'USA' },
  { value: 'GBR', label: '🇬🇧 Royaume-Uni', code: 'GBR' },
  { value: 'CAN', label: '🇨🇦 Canada', code: 'CAN' },
  { value: 'DEU', label: '🇩🇪 Allemagne', code: 'DEU' },
  { value: 'NLD', label: '🇳🇱 Pays-Bas', code: 'NLD' },
  { value: 'BEL', label: '🇧🇪 Belgique', code: 'BEL' },
  { value: 'CHE', label: '🇨🇭 Suisse', code: 'CHE' },
  { value: 'ITA', label: '🇮🇹 Italie', code: 'ITA' },
  { value: 'ESP', label: '🇪🇸 Espagne', code: 'ESP' },
  { value: 'PRT', label: '🇵🇹 Portugal', code: 'PRT' },
  { value: 'BRA', label: '🇧🇷 Brésil', code: 'BRA' },
  { value: 'ARG', label: '🇦🇷 Argentine', code: 'ARG' },
  { value: 'MEX', label: '🇲🇽 Mexique', code: 'MEX' },
];

function PassportPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [form] = Form.useForm();
  const [stats, setStats] = useState({
    total: 0,
    complete: 0,
    missing: 0,
    expiringSoon: 0,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Fetch employees
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Quick copy functions
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copié !`);
  };

  const copyEmployeeData = (employee) => {
    const data = `${employee.name || ''}\t${employee.first_name || ''}\t${employee.birth_date ? dayjs(employee.birth_date).format('DD/MM/YYYY') : ''}\t${employee.birth_place || ''}\t${employee.passport_number || ''}\t${employee.passport_expiry ? dayjs(employee.passport_expiry).format('DD/MM/YYYY') : ''}\t${employee.nationality || ''}`;
    copyToClipboard(data, 'Ligne complète');
  };

  const copySelectedForClairance = () => {
    const selectedEmployees = employees.filter(emp => selectedRowKeys.includes(emp.id));

    if (selectedEmployees.length === 0) {
      message.warning('Sélectionnez des membres d\'équipage');
      return;
    }

    const crew = selectedEmployees.map(emp => ({
      nom: emp.name || '',
      prenom: emp.first_name || '',
      birthDate: emp.birth_date ? dayjs(emp.birth_date).format('DD/MM/YYYY') : '',
      birthPlace: emp.birth_place || '',
      passport: emp.passport_number || '',
      nationality: emp.nationality || 'FRA'
    }));

    const data = {
      formData: {
        departureDate: dayjs().format('DD/MM/YYYY'),
        departureTime: '10:00',
        activity: 'charter',
        berth: 'quai_republique'
      },
      crew,
      passengers: []
    };

    localStorage.setItem('msp_gustavia_complete', JSON.stringify(data));
    copyToClipboard(JSON.stringify(data, null, 2), 'Données clairance');
    message.success(`${selectedEmployees.length} membres copiés pour clairance`);
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active')
        .order('name');

      if (error) throw error;

      setEmployees(data || []);
      calculateStats(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      message.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const total = data.length;
    const complete = data.filter(e =>
      e.first_name && e.birth_date && e.passport_number && e.nationality
    ).length;
    const missing = total - complete;

    // Passeports expirant dans moins de 6 mois
    const sixMonthsFromNow = dayjs().add(6, 'month');
    const expiringSoon = data.filter(e =>
      e.passport_expiry && dayjs(e.passport_expiry).isBefore(sixMonthsFromNow)
    ).length;

    setStats({ total, complete, missing, expiringSoon });
  };

  const handleEdit = (record) => {
    setEditingEmployee(record);
    form.setFieldsValue({
      ...record,
      birth_date: record.birth_date ? dayjs(record.birth_date) : null,
      passport_expiry: record.passport_expiry ? dayjs(record.passport_expiry) : null,
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const updateData = {
        name: values.name,
        first_name: values.first_name,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
        birth_place: values.birth_place,
        passport_number: values.passport_number,
        passport_expiry: values.passport_expiry ? values.passport_expiry.format('YYYY-MM-DD') : null,
        nationality: values.nationality,
      };

      const { error } = await supabase
        .from('employees')
        .update(updateData)
        .eq('id', editingEmployee.id);

      if (error) throw error;

      message.success('Informations mises à jour');
      setModalVisible(false);
      form.resetFields();
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error) {
      console.error('Error updating employee:', error);
      message.error('Erreur lors de la mise à jour');
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingEmployee(null);
  };

  // Check if passport is expiring soon
  const isExpiringSoon = (expiryDate) => {
    if (!expiryDate) return false;
    const sixMonthsFromNow = dayjs().add(6, 'month');
    return dayjs(expiryDate).isBefore(sixMonthsFromNow);
  };

  // Table columns
  const columns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 180,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Prénom',
      dataIndex: 'first_name',
      key: 'first_name',
      width: 150,
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (text) => (
        <Tag style={{ borderRadius: radius.md }}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Date de naissance',
      dataIndex: 'birth_date',
      key: 'birth_date',
      width: 140,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : <Text type="secondary">-</Text>,
    },
    {
      title: 'Lieu de naissance',
      dataIndex: 'birth_place',
      key: 'birth_place',
      width: 150,
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'N° Passeport',
      dataIndex: 'passport_number',
      key: 'passport_number',
      width: 140,
      render: (text) => text ? (
        <Text strong code>{text}</Text>
      ) : (
        <Text type="secondary">-</Text>
      ),
    },
    {
      title: 'Date d\'expiration',
      dataIndex: 'passport_expiry',
      key: 'passport_expiry',
      width: 140,
      render: (date, record) => {
        if (!date) return <Text type="secondary">-</Text>;
        const expiring = isExpiringSoon(date);
        return (
          <Space>
            {expiring && <ExclamationCircleOutlined style={{ color: colors.accentError }} />}
            <Text style={{ color: expiring ? colors.accentError : colors.textPrimary }}>
              {dayjs(date).format('DD/MM/YYYY')}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Nationalité',
      dataIndex: 'nationality',
      key: 'nationality',
      width: 140,
      render: (code) => {
        const nat = NATIONALITIES.find(n => n.code === code);
        return nat ? nat.label : (code || <Text type="secondary">-</Text>);
      },
    },
    {
      title: 'Statut',
      key: 'status',
      width: 120,
      render: (_, record) => {
        const isComplete = record.first_name && record.birth_date && record.passport_number && record.nationality;
        return (
          <Tag color={isComplete ? 'success' : 'warning'} style={{ borderRadius: radius.md }}>
            {isComplete ? '✓ Complet' : '⚠ Incomplet'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Copier la ligne">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => copyEmployeeData(record)}
            />
          </Tooltip>
          <Tooltip title="Copier N° Passeport">
            <Button
              type="link"
              onClick={() => copyToClipboard(record.passport_number || '', 'N° Passeport')}
              disabled={!record.passport_number}
              style={{ fontSize: '12px' }}
            >
              📋
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIcon}>📘</div>
            <div>
              <Title level={3} style={{ margin: 0, color: colors.textPrimary }}>
                Passeports Équipage
              </Title>
              <Text style={{ color: colors.textMuted }}>
                Gestion des informations passeport de l'équipage
              </Text>
            </div>
          </div>
          <Space>
            {selectedRowKeys.length > 0 && (
              <Button
                icon={<CheckCircleOutlined />}
                onClick={copySelectedForClairance}
                style={styles.primaryBtn}
              >
                Copier pour Clairance ({selectedRowKeys.length})
              </Button>
            )}
          </Space>
        </div>

        {/* Stats */}
        <div style={styles.statsCard}>
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>{stats.total}</div>
              <div style={styles.statLabel}>Total équipage</div>
            </div>
            <div style={styles.statItem}>
              <div style={{ ...styles.statNumber, color: colors.accentSuccess }}>{stats.complete}</div>
              <div style={styles.statLabel}>Profils complets</div>
            </div>
            <div style={styles.statItem}>
              <div style={{ ...styles.statNumber, color: colors.accentOrange }}>{stats.missing}</div>
              <div style={styles.statLabel}>À compléter</div>
            </div>
            <div style={styles.statItem}>
              <div style={{ ...styles.statNumber, color: colors.accentError }}>{stats.expiringSoon}</div>
              <div style={styles.statLabel}>Expirent bientôt</div>
            </div>
          </div>
        </div>

        {/* Alert for expiring passports */}
        {stats.expiringSoon > 0 && (
          <Alert
            message="Passeports expirant bientôt"
            description={`${stats.expiringSoon} passeport(s) expirent dans moins de 6 mois. Vérifiez les dates d'expiration.`}
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: spacing.lg, borderRadius: radius.lg }}
          />
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={employees}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            selections: [
              Table.SELECTION_ALL,
              Table.SELECTION_INVERT,
              Table.SELECTION_NONE,
              {
                key: 'crew',
                text: 'Sélectionner équipage (Capitaine + Marin)',
                onSelect: () => {
                  const crewKeys = employees
                    .filter(e => e.role?.toLowerCase().includes('capitaine') || e.role?.toLowerCase().includes('marin'))
                    .map(e => e.id);
                  setSelectedRowKeys(crewKeys);
                },
              },
            ],
          }}
          scroll={{ x: 1500 }}
          pagination={{
            pageSize: 50,
            showTotal: (total) => `${total} membres d'équipage`,
          }}
          style={{
            background: colors.bgSecondary,
            borderRadius: radius.xl,
            border: `1px solid ${colors.borderLight}`,
          }}
        />

        {/* Edit Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              <FileTextOutlined />
              <span>Modifier les informations</span>
            </div>
          }
          open={modalVisible}
          onOk={handleSave}
          onCancel={handleCancel}
          width={700}
          okText="Enregistrer"
          cancelText="Annuler"
          okButtonProps={{ style: styles.primaryBtn }}
        >
          <Form
            form={form}
            layout="vertical"
            style={{ marginTop: spacing.lg }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
              <Form.Item
                name="name"
                label="Nom de famille"
                rules={[{ required: true, message: 'Nom de famille requis' }]}
              >
                <Input placeholder="Nom de famille" style={{ textTransform: 'uppercase' }} />
              </Form.Item>

              <Form.Item
                name="first_name"
                label="Prénom"
                rules={[{ required: true, message: 'Prénom requis' }]}
              >
                <Input placeholder="Prénom" />
              </Form.Item>

              <Form.Item
                name="birth_date"
                label="Date de naissance"
                rules={[{ required: true, message: 'Date de naissance requise' }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="JJ/MM/AAAA"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="birth_place"
                label="Lieu de naissance"
                rules={[{ required: true, message: 'Lieu de naissance requis' }]}
              >
                <Input placeholder="Ville, Pays" />
              </Form.Item>

              <Form.Item
                name="passport_number"
                label="N° de passeport"
                rules={[{ required: true, message: 'N° de passeport requis' }]}
              >
                <Input placeholder="Ex: 12AB34567" style={{ textTransform: 'uppercase' }} />
              </Form.Item>

              <Form.Item
                name="passport_expiry"
                label="Date d'expiration"
                rules={[{ required: true, message: 'Date d\'expiration requise' }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder="JJ/MM/AAAA"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="nationality"
                label="Nationalité"
                rules={[{ required: true, message: 'Nationalité requise' }]}
              >
                <Select placeholder="Sélectionner" showSearch optionFilterProp="children">
                  {NATIONALITIES.map(nat => (
                    <Option key={nat.code} value={nat.code}>
                      {nat.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>
    </div>
  );
}

export default PassportPage;
